import React, { useMemo } from 'react';
import type { Todo } from '@signa/shared';
import { useTodoStore } from '../../stores/todo-store';
import { useEmailStore } from '../../stores/email-store';
import { PersonAvatar } from '../today/PersonAvatar';
import { FocusHeader } from './FocusHeader';
import { FocusAction } from './FocusAction';
import { CatchupPane } from './CatchupPane';
import type { MemoryContext } from '../../lib/memory-api';

/**
 * Todo focus: left content, right catch-up. Subtasks are child todos
 * (parentId === this id). Related email is resolved via sourceEmailId.
 */

interface TodoFocusProps {
  todo: Todo;
  context: MemoryContext | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}

export function TodoFocus({ todo, context, loading, error, onClose }: TodoFocusProps) {
  const allTodos = useTodoStore((s) => s.todos);
  const emails = useEmailStore((s) => s.emails);

  const subtasks = useMemo(
    () => allTodos.filter((t) => t.parentId === todo.id),
    [allTodos, todo.id]
  );

  const sourceEmail = useMemo(
    () =>
      todo.sourceEmailId
        ? emails.find((e) => e.id === todo.sourceEmailId) ?? null
        : null,
    [emails, todo.sourceEmailId]
  );

  const dueLabel = formatDue(todo.dueDate);
  const metaItems = dueLabel ? ['Todo', `Due ${dueLabel}`] : ['Todo'];

  return (
    <>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <FocusHeader onClose={onClose} />
        <div
          style={{
            padding: '20px 44px 80px',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              letterSpacing: '1.4px',
              textTransform: 'uppercase',
              color: 'var(--ink-faint)',
              marginBottom: 10,
            }}
          >
            {metaItems.map((item, index) => (
              <React.Fragment key={item}>
                {index > 0 && (
                  <span
                    aria-hidden
                    style={{
                      width: 1,
                      height: 10,
                      background: 'var(--rule)',
                      display: 'inline-block',
                    }}
                  />
                )}
                <span>{item}</span>
              </React.Fragment>
            ))}
          </div>

          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 30,
              letterSpacing: '-0.5px',
              lineHeight: 1.2,
              color: 'var(--ink)',
              marginBottom: 28,
              fontWeight: 400,
              margin: '0 0 28px 0',
            }}
          >
            {todo.title}
          </h1>

          {todo.description && (
            <p
              style={{
                fontSize: 15.5,
                lineHeight: 1.65,
                color: 'var(--ink)',
                marginBottom: 24,
                textWrap: 'pretty',
              }}
            >
              {todo.description}
            </p>
          )}

          {subtasks.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <SectionLabel>Subtasks</SectionLabel>
              {subtasks.map((s, i) => (
                <div
                  key={s.id}
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                    padding: '10px 0',
                    borderTop: i === 0 ? 'none' : '1px solid var(--rule)',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      border: `1.5px solid ${
                        s.completedAt ? 'var(--accent)' : 'var(--ink-faint)'
                      }`,
                      background: s.completedAt ? 'var(--accent)' : 'transparent',
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 14.5,
                      color: s.completedAt ? 'var(--ink-faint)' : 'var(--ink)',
                      textDecoration: s.completedAt ? 'line-through' : 'none',
                    }}
                  >
                    {s.title}
                  </span>
                </div>
              ))}
            </div>
          )}

          {sourceEmail && (
            <div style={{ marginTop: 28 }}>
              <SectionLabel>Related conversation</SectionLabel>
              <div
                style={{
                  padding: '12px 14px',
                  border: '1px solid var(--rule)',
                  borderRadius: 10,
                  background: 'var(--card)',
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 4,
                  }}
                >
                  <PersonAvatar
                    name={sourceEmail.fromName || sourceEmail.fromEmail}
                    email={sourceEmail.fromEmail}
                    size={20}
                  />
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>
                    {sourceEmail.fromName || sourceEmail.fromEmail}
                  </span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 11,
                      color: 'var(--ink-faint)',
                    }}
                  >
                    {formatTime(sourceEmail.receivedAt)}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 13.5,
                    color: 'var(--ink-soft)',
                    lineHeight: 1.5,
                  }}
                >
                  {sourceEmail.snippet}
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop: 32, display: 'flex', gap: 8 }}>
            <FocusAction accent>Mark done</FocusAction>
            <FocusAction>Schedule</FocusAction>
            <FocusAction>Delegate</FocusAction>
          </div>
        </div>
      </div>

      <CatchupPane
        kind="todo"
        context={context}
        loading={loading}
        error={error}
        todoNarrative={buildTodoNarrative(todo)}
      />
    </>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        letterSpacing: '1.4px',
        textTransform: 'uppercase',
        color: 'var(--ink-faint)',
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

function formatDue(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round(
    (d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff > 0 && diff < 7) return d.toLocaleDateString([], { weekday: 'long' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    now.getFullYear() === d.getFullYear() &&
    now.getMonth() === d.getMonth() &&
    now.getDate() === d.getDate();
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function buildTodoNarrative(todo: Todo): React.ReactNode {
  const due = formatDue(todo.dueDate);
  if (todo.completedAt) return 'Completed. Nothing further to action.';
  if (due === 'Today') {
    return `Due today. ${todo.description ? 'Context from the PA is attached below.' : 'No further context stored yet.'}`;
  }
  if (due === 'Yesterday') {
    return 'This is running late and was due yesterday. Consider rescheduling or unblocking.';
  }
  if (due) {
    return `Scheduled for ${due}. The PA will surface it in your morning briefing as the date approaches.`;
  }
  return 'No due date. Add one to have the PA remind you.';
}
